import { useCallback, useEffect } from 'react'

import { useSessionStore } from '@/lib/auth/session-store'

import {
  formatPersonName,
  type FriendIdIndex,
  type PersonNameParts,
} from './person-name'
import {
  useFriendIndexStore,
  useFriendIndex,
} from './friend-index-store'

export type FormattedPerson = PersonNameParts

/**
 * Binds the D-101 name-privacy rule to React: the first call in a tree also
 * triggers the (cached) friend-list fetch so abbreviated names can upgrade to
 * full names for friends once the index resolves.
 */
export function usePersonNameFormatter(
  /**
   * When a surface already knows the pair directly (e.g. the public-profile
   * relationship endpoint says "accepted"), it can override the shared
   * friend index without waiting for the list.
   */
  friendIdsOverride?: FriendIdIndex | null,
) {
  const friendIds = useFriendIndex()
  useEffect(() => {
    void useFriendIndexStore.getState().load()
  }, [])
  return useCallback(
    (person: FormattedPerson | null | undefined) =>
      formatPersonName(person, {
        friendIds: friendIdsOverride ?? friendIds,
        viewerId: useSessionStore.getState().user?.id ?? null,
      }),
    [friendIds, friendIdsOverride],
  )
}
